/**
 * Tour guiado de primeiro acesso (react-joyride).
 *
 * Como funciona:
 *   - O `TourProvider` envolve o app autenticado e escuta mudanças de rota
 *   - Quando o usuário entra pela primeira vez (firstLoginDone=false),
 *     o tour da página atual roda automaticamente. Ao terminar TODO o tour
 *     (ou se o usuário clicar em "Pular"), chama POST /auth/first-login-done.
 *   - Em qualquer momento o botão "Iniciar tour" nas Preferências pode
 *     re-disparar o tour da página atual.
 *
 * Os steps de cada página estão em `tourSteps.ts`. Cada step usa um seletor
 * `data-tour="..."` para encontrar o elemento na tela.
 */
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { Joyride as JoyrideRaw, STATUS as STATUS_RAW } from "react-joyride";
const Joyride: any = JoyrideRaw;
const STATUS: any = STATUS_RAW;
type EventData = any;
type Step = any;
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { API_URL, TOKEN_KEY } from "../../constants";
import { tourSteps, TourKey, getTourKeyForPath, TOUR_ORDER } from "./tourSteps";

interface TourCtx {
  startTour: (key?: TourKey) => void;
  startFullTour: () => void;
  isRunning: boolean;
}

const TourContext = createContext<TourCtx>({
  startTour: () => {},
  startFullTour: () => {},
  isRunning: false,
});

export const useTour = () => useContext(TourContext);

const STORAGE_KEY = "splitfacil_first_login_done";
const SEEN_TABS_KEY = "splitfacil_tour_seen_tabs";

// Verifica via /auth/me se firstLoginDone está marcado no backend (e cacheia local)
const isFirstLoginDone = (): boolean => {
  return localStorage.getItem(STORAGE_KEY) === "true";
};

const markFirstLoginDoneLocal = () => {
  localStorage.setItem(STORAGE_KEY, "true");
};

// Set de chaves de tour ja vistas (uma por aba/pagina).
// Marcado assim que o tour daquela aba aparece, evitando que ele
// reapareça toda vez que o usuario volta para a mesma aba.
const getSeenTabs = (): Set<string> => {
  try {
    const raw = localStorage.getItem(SEEN_TABS_KEY);
    if (!raw) return new Set();
    const arr = JSON.parse(raw);
    return new Set(Array.isArray(arr) ? arr : []);
  } catch {
    return new Set();
  }
};

const markTabSeen = (key: string) => {
  const seen = getSeenTabs();
  seen.add(key);
  localStorage.setItem(SEEN_TABS_KEY, JSON.stringify(Array.from(seen)));
};

const isTabSeen = (key: string): boolean => {
  return getSeenTabs().has(key);
};

const markFirstLoginDoneRemote = async () => {
  try {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) return;
    await axios.post(
      `${API_URL}/auth/first-login-done`,
      {},
      { headers: { Authorization: `Bearer ${token}` } }
    );
  } catch {
    // silencioso
  }
};

export const TourProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [run, setRun] = useState(false);
  const [activeKey, setActiveKey] = useState<TourKey | null>(null);
  // Modo "tour completo": vai navegando entre páginas
  const [fullTourMode, setFullTourMode] = useState(false);

  const currentSteps: Step[] = useMemo(() => {
    if (!activeKey) return [];
    return tourSteps[activeKey] || [];
  }, [activeKey]);

  // Dispara automaticamente o tour da pagina atual quando:
  //   1. ainda nao marcou firstLoginDone global, E
  //   2. aquela aba especificamente ainda nao foi vista antes.
  // Assim que o tour dispara, marca a aba como vista — se o usuario sair
  // sem terminar o tour, ele NAO reaparece naquela aba.
  useEffect(() => {
    if (isFirstLoginDone()) return;
    const key = getTourKeyForPath(location.pathname);
    if (!key || !tourSteps[key]?.length) return;
    if (isTabSeen(key)) return;

    setRun(false);
    const t = setTimeout(() => {
      setActiveKey(key);
      setFullTourMode(true);
      markTabSeen(key);
      setTimeout(() => setRun(true), 50);
    }, 700);
    return () => clearTimeout(t);
  }, [location.pathname]);

  const startTour = useCallback(
    (key?: TourKey) => {
      const target = key || getTourKeyForPath(location.pathname);
      if (!target) return;
      setActiveKey(target);
      setRun(false); // reset
      setFullTourMode(false);
      setTimeout(() => setRun(true), 100);
    },
    [location.pathname]
  );

  const startFullTour = useCallback(() => {
    setFullTourMode(true);
    setActiveKey(TOUR_ORDER[0]);
    navigate("/");
    setTimeout(() => setRun(true), 800);
  }, [navigate]);

  const skipAll = useCallback(() => {
    setRun(false);
    setFullTourMode(false);
    setActiveKey(null);
    markFirstLoginDoneLocal();
    markFirstLoginDoneRemote();
  }, []);

  const handleCallback = useCallback(
    (data: EventData) => {
      const { status, action } = data;
      const finished: string[] = [STATUS.FINISHED, STATUS.SKIPPED];

      // Clique no X de fechar = mesma coisa que "Pular tour"
      const closedByX = action === "close";

      if (!finished.includes(status) && !closedByX) return;

      setRun(false);

      // Usuario clicou em "Pular tour" ou no X → marca como visto e para tudo
      if (status === STATUS.SKIPPED || closedByX) {
        skipAll();
        return;
      }

      // Concluiu (FINISHED) o tour da pagina atual
      if (fullTourMode && activeKey) {
        const idx = TOUR_ORDER.indexOf(activeKey);
        const next = TOUR_ORDER[idx + 1];
        if (!next) {
          // Chegou ao fim da jornada completa → marca como visto
          setFullTourMode(false);
          setActiveKey(null);
          markFirstLoginDoneLocal();
          markFirstLoginDoneRemote();
          return;
        }
        // Avanca pra proxima pagina automaticamente
        const nextRoute = tourSteps[next]?.[0]?.data?.route;
        if (nextRoute) navigate(nextRoute);
        setActiveKey(next);
        setTimeout(() => setRun(true), 900);
      } else {
        // Tour disparado manualmente (botao "Iniciar tour" nas Preferencias)
        // termina apenas naquela pagina, sem mexer no flag de primeiro acesso.
        setActiveKey(null);
      }
    },
    [fullTourMode, activeKey, navigate]
  );

  return (
    <TourContext.Provider value={{ startTour, startFullTour, isRunning: run }}>
      {children}
      {activeKey && run && (
        <button
          type="button"
          onClick={skipAll}
          style={{
            position: "fixed",
            top: 16,
            right: 16,
            zIndex: 10010,
            background: "#dc2626",
            color: "#ffffff",
            border: "none",
            borderRadius: 999,
            padding: "10px 18px",
            fontSize: 13,
            fontWeight: 600,
            boxShadow: "0 10px 30px rgba(220, 38, 38, 0.35)",
            cursor: "pointer",
            letterSpacing: 0.2,
          }}
          aria-label="Pular tour guiado"
        >
          Pular tour guiado ×
        </button>
      )}
      {activeKey && currentSteps.length > 0 && (
        <Joyride
          steps={currentSteps}
          run={run}
          continuous
          showProgress
          showSkipButton
          hideCloseButton={false}
          scrollToFirstStep
          disableScrolling={false}
          spotlightPadding={6}
          callback={handleCallback}
          locale={{
            back: "Voltar",
            close: "Fechar",
            last: fullTourMode ? "Próxima página" : "Concluir",
            next: "Próximo",
            skip: "Pular tour",
          }}
          styles={({
            options: {
              primaryColor: "#2563eb",
              backgroundColor: "#ffffff",
              textColor: "#0f172a",
              arrowColor: "#ffffff",
              overlayColor: "rgba(15, 23, 42, 0.55)",
              zIndex: 10000,
              width: 400,
            },
            tooltip: {
              borderRadius: 16,
              padding: "22px 24px 18px",
              boxShadow: "0 20px 60px rgba(15, 23, 42, 0.2)",
            },
            tooltipContainer: {
              textAlign: "left",
            },
            tooltipTitle: {
              fontSize: 17,
              fontWeight: 700,
              marginBottom: 6,
            },
            tooltipContent: {
              fontSize: 14,
              lineHeight: 1.55,
              color: "#475569",
              paddingBottom: 4,
            },
            tooltipFooter: {
              marginTop: 16,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            },
            buttonNext: {
              backgroundColor: "#2563eb",
              borderRadius: 10,
              fontSize: 14,
              fontWeight: 600,
              padding: "10px 18px",
              color: "#ffffff",
            },
            buttonBack: {
              color: "#475569",
              marginRight: 8,
              fontSize: 14,
              fontWeight: 500,
            },
            buttonSkip: {
              color: "#dc2626",
              fontSize: 13,
              fontWeight: 600,
              padding: "8px 14px",
              border: "1px solid #fecaca",
              borderRadius: 10,
              background: "#fef2f2",
            },
            buttonClose: {
              display: "block",
              width: 14,
              height: 14,
              top: 14,
              right: 14,
              color: "#94a3b8",
            },
          }) as any}
        />
      )}
    </TourContext.Provider>
  );
};
